// src/lib/checkout-link.ts
//
// Parses the deep-link cart parameters on /payment/checkout:
//
//   /payment/checkout?products=<sku>:<qty>,<sku>:<qty>&coupon=<code>
//
// This exists for Meta's shop "checkout URL" (Commerce Manager → Set up your
// shop → Add checkout URL): a buyer taps Buy inside the Facebook/Instagram shop
// and Meta redirects them here with their Meta-side cart in the query string.
// Meta will not let a shop publish without it, and it periodically re-tests the
// URL. The `sku` values are catalog content ids — for us, Square catalog
// VARIATION SKUs, the same values scripts/export-meta-catalog.js writes to the
// `id` column of the feed. Anything else silently fails to resolve.
//
// It is also useful on its own: any "add these to cart" link for email or SMS
// can use the same shape.
//
// TRUST: none of this is authoritative. A hand-edited link can only choose which
// catalog items land in the buyer's own cart — prices are resolved server-side
// from the Square catalog in create-order, and the coupon is re-resolved through
// utils/promo-codes.js. That's why quantities are clamped rather than verified:
// the cap is there to stop a silly link (qty=99999) producing an absurd order,
// not to protect money.

export interface CheckoutLinkItem {
  sku: string;
  quantity: number;
}

/** Guard rails for a URL-supplied cart. Generous, but bounded. */
export const MAX_LINK_ITEMS = 20;
export const MAX_LINK_QUANTITY = 25;

// Square SKUs are alphanumeric with _ - . separators. Anything else is dropped
// rather than sanitized, so a malformed entry can't half-match a real SKU.
const SKU_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;

/**
 * Parse the `products` query parameter into a de-duplicated item list.
 *
 * Accepts `SKU` (implicit quantity 1) or `SKU:QTY`. Unparseable entries are
 * skipped silently — a partly-broken link should still deliver the items it can
 * rather than dumping the buyer on an empty checkout.
 */
export const parseProductsParam = (
  raw: string | null | undefined,
): CheckoutLinkItem[] => {
  if (!raw) return [];

  const merged = new Map<string, number>();

  for (const entry of raw.split(",")) {
    const [rawSku, rawQty] = entry.split(":");
    const sku = (rawSku ?? "").trim();
    if (!SKU_PATTERN.test(sku)) continue;

    let quantity = 1;
    if (rawQty !== undefined) {
      const parsed = Number.parseInt(rawQty.trim(), 10);
      // A present-but-garbage quantity ("abc", "0", "-2") falls back to 1 rather
      // than dropping the product the buyer actually asked for.
      quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    const previous = merged.get(sku) ?? 0;
    merged.set(sku, Math.min(previous + quantity, MAX_LINK_QUANTITY));
    if (merged.size >= MAX_LINK_ITEMS) break;
  }

  return Array.from(merged, ([sku, quantity]) => ({ sku, quantity }));
};

/** Normalize the `coupon` parameter. Empty/absent → null. */
export const parseCouponParam = (
  raw: string | null | undefined,
): string | null => {
  const code = (raw ?? "").trim().toUpperCase();
  return /^[A-Z0-9._-]{1,40}$/.test(code) ? code : null;
};
